import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import API_URL from '../api';
import Layout from '../components/Layout';

export default function Customers() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [err, setErr] = useState('');

  // Import state
  const fileInput = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  function blankForm() {
    return { customer_type: 'company', company_name: '', first_name: '', last_name: '', display_name: '', email: '', phone: '', billing_address: '' };
  }

  useEffect(() => { load(); }, []);

  // If we arrived here from the New Job picker, auto-open the New Customer
  // form pre-filled with whatever was typed.
  useEffect(() => {
    const prefill = sessionStorage.getItem('createCustomerPrefill');
    if (prefill !== null) {
      const f = blankForm();
      f.customer_type = 'company';
      f.company_name = prefill;
      f.display_name = prefill;
      setForm(f);
      setEditId(null);
      setErr('');
      setShowForm(true);
      sessionStorage.removeItem('createCustomerPrefill');
    }
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`, { headers });
      setCustomers(res.data);
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const openNew = () => { setForm(blankForm()); setEditId(null); setErr(''); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      customer_type: c.customer_type || 'company',
      company_name: c.company_name || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      display_name: c.display_name || '',
      email: c.email || '',
      phone: c.phone || '',
      billing_address: c.billing_address || ''
    });
    setEditId(c.id); setErr(''); setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      let saved;
      if (editId) {
        const res = await axios.put(`${API_URL}/api/customers/${editId}`, form, { headers });
        saved = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/customers`, form, { headers });
        saved = res.data;
      }
      setShowForm(false);

      // If we came from New Job, hand the new customer back and return.
      if (!editId && sessionStorage.getItem('returnToNewJob')) {
        sessionStorage.removeItem('returnToNewJob');
        sessionStorage.setItem('newJobReturn', JSON.stringify({
          customer_id: saved.id,
          customer_name: saved.display_name
        }));
        navigate('/');
        return;
      }

      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  // ---- CSV / Excel import ----
  const triggerImport = () => { setImportMsg(''); fileInput.current.click(); };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportMsg('Reading file...');
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      // Map QuickBooks columns -> our fields
      const toImport = [];
      for (const r of rows) {
        const name = String(r['Name'] || '').trim();
        if (!name) continue; // skip blank rows
        if (name.includes(':')) continue; // skip sub-customers/projects

        const address = [r['Street Address'], r['City'], r['State'], r['Zip']]
          .map(x => String(x || '').trim())
          .filter(Boolean)
          .join(', ');

        const companyName = String(r['Company name'] || '').trim();

        toImport.push({
          customer_type: companyName ? 'company' : 'person',
          company_name: companyName || null,
          first_name: null,
          last_name: null,
          display_name: name,
          email: String(r['Email'] || '').trim() || null,
          phone: String(r['Phone'] || '').trim() || null,
          billing_address: address || null
        });
      }

      if (toImport.length === 0) {
        setImportMsg('No customers found in that file.');
        setImporting(false);
        e.target.value = '';
        return;
      }

      setImportMsg(`Importing ${toImport.length} customers...`);
      const res = await axios.post(`${API_URL}/api/customers/import`, { customers: toImport }, { headers });
      setImportMsg(`Done — imported ${res.data.imported}, skipped ${res.data.skipped}.`);
      load();
    } catch (e) {
      setImportMsg('Import failed: ' + (e.response?.data?.error || e.message));
    }
    setImporting(false);
    e.target.value = '';
  };

  // TEMPORARY wipe-all (remove before customers use this)
  const wipeAll = async () => {
    if (!window.confirm('Delete ALL your customers? This cannot be undone.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/wipe-all-customers`, {}, { headers });
      alert('Deleted ' + res.data.deleted + ' customers.');
      load();
    } catch (e) {
      alert('Wipe failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const filtered = customers.filter(c =>
    (c.display_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={{textAlign:'center',padding:'60px'}}>Loading...</div>;

  return (
    <Layout>
      <div style={styles.topbar}>
        <h1 style={styles.pageTitle}>Customers</h1>
        <div style={{display:'flex',gap:'10px'}}>
          <button onClick={wipeAll} style={{...styles.btnGhost, color:'#C0392B', borderColor:'#C0392B'}}>Wipe All</button>
          <button onClick={triggerImport} disabled={importing} style={styles.btnGhost}>
            {importing ? 'Importing...' : 'Import CSV/Excel'}
          </button>
          <button onClick={openNew} style={styles.btnPrimary}>+ New Customer</button>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xls,.xlsx"
          style={{display:'none'}}
          onChange={handleFile}
        />
      </div>

      <div style={styles.content}>
        {importMsg ? <div style={styles.infoBox}>{importMsg}</div> : null}

        <div style={styles.card}>
          <input
            style={styles.searchInput}
            placeholder="Search customers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Email</th><th style={styles.th}>Phone</th><th style={styles.th}>Type</th><th style={styles.th}></th></tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={styles.td}><strong>{c.display_name}</strong></td>
                  <td style={styles.td}>{c.email || '—'}</td>
                  <td style={styles.td}>{c.phone || '—'}</td>
                  <td style={styles.td}>{c.customer_type === 'person' ? 'Person' : 'Company'}</td>
                  <td style={styles.td}><button onClick={() => openEdit(c)} style={styles.btnTiny}>Edit</button></td>
                </tr>
              ))}
              {filtered.length === 0 ? <tr><td style={styles.td} colSpan="5">No customers found.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={styles.overlay} onClick={() => setShowForm(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.cardTitle}>{editId ? 'Edit Customer' : 'New Customer'}</h3>
            <form onSubmit={save}>
              {err ? <div style={styles.errorBox}>{err}</div> : null}

              <label style={styles.label}>Type</label>
              <select style={styles.input} value={form.customer_type} onChange={e => setForm({...form, customer_type: e.target.value})}>
                <option value="company">Company</option>
                <option value="person">Person</option>
              </select>

              {form.customer_type === 'company' ? (
                <>
                  <label style={styles.label}>Company Name</label>
                  <input style={styles.input} value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value, display_name: e.target.value})} placeholder="Goldstream Construction LLC" />
                </>
              ) : (
                <>
                  <div style={{display:'flex',gap:'10px'}}>
                    <div style={{flex:1}}>
                      <label style={styles.label}>First Name</label>
                      <input style={styles.input} value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value, display_name: `${e.target.value} ${form.last_name}`.trim()})} />
                    </div>
                    <div style={{flex:1}}>
                      <label style={styles.label}>Last Name</label>
                      <input style={styles.input} value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value, display_name: `${form.first_name} ${e.target.value}`.trim()})} />
                    </div>
                  </div>
                </>
              )}

              <label style={styles.label}>Display Name</label>
              <input style={styles.input} value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="How this customer appears in lists" />

              <div style={{display:'flex',gap:'10px'}}>
                <div style={{flex:1}}>
                  <label style={styles.label}>Email</label>
                  <input style={styles.input} value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
                <div style={{flex:1}}>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                </div>
              </div>

              <label style={styles.label}>Billing Address</label>
              <input style={styles.input} value={form.billing_address} onChange={e => setForm({...form, billing_address: e.target.value})} placeholder="123 Main St, City, UT" />

              <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                <button type="submit" style={styles.btnPrimary}>{editId ? 'Save Changes' : 'Create Customer'}</button>
                <button type="button" onClick={() => setShowForm(false)} style={styles.btnGhost}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  topbar: { background: '#fff', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e6e9ed' },
  pageTitle: { color: '#2E4057', margin: 0, fontSize: '22px' },
  content: { padding: '32px', maxWidth: '900px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 16px', color: '#2E4057', fontSize: '18px' },
  searchInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#888', textTransform: 'uppercase', background: '#f9f9f9', letterSpacing: '0.5px' },
  td: { padding: '10px 12px', fontSize: '14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
  btnPrimary: { padding: '10px 22px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  btnGhost: { padding: '10px 22px', background: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  btnTiny: { padding: '5px 12px', background: '#fff', color: '#2E4057', border: '1px solid #2E4057', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
  label: { display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '60px', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '10px', padding: '28px', width: '520px', maxWidth: '90%', maxHeight: '80vh', overflowY: 'auto' },
  errorBox: { background: '#FDEDEC', color: '#C0392B', padding: '12px', borderRadius: '6px', fontSize: '14px', marginBottom: '16px' },
  infoBox: { background: '#EAF2F8', color: '#21618C', padding: '12px', borderRadius: '6px', fontSize: '14px', marginBottom: '16px' },
};